/**
 * @fileoverview Login page with email/password credentials.
 * Calls customLogin from AuthContext and handles error display.
 * Provides links to forgot password and signup pages.
 */

import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PasswordInput from '../components/PasswordInput';

/**
 * Login page component.
 * Renders email and password fields, submits via customLogin from auth context.
 * Redirects authenticated super admins to the admin page, others to /.
 *
 * @returns {JSX.Element} The login page or a redirect
 */
function Login() {
  const { authenticated, customLogin, isSuperAdmin, adminPath } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendStatus, setResendStatus] = useState(''); // '', 'sending', 'sent'

  // Already authenticated — redirect super admins to admin page, others to home
  if (authenticated) {
    if (isSuperAdmin && adminPath) {
      return <Navigate to={adminPath} replace />;
    }
    return <Navigate to="/" replace />;
  }

  /**
   * Handle login form submission.
   * Validates inputs, calls customLogin, and handles errors.
   *
   * @param {React.FormEvent} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setResendStatus('');

    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }

    setSubmitting(true);
    try {
      const result = await customLogin(email.trim().toLowerCase(), password);
      if (!result.success) {
        if (result.error === 'account_not_verified') {
          setUnverified(true);
          setError('Your account has not been verified.');
        } else {
          setError(result.error || 'Invalid email or password');
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Resend the verification email for the current email address.
   */
  const handleResendVerification = async () => {
    if (!email.trim()) return;
    setResendStatus('sending');
    try {
      await api.resendVerification({ email: email.trim().toLowerCase() });
    } catch {
      // Endpoint always returns 200, but handle network errors
    }
    setResendStatus('sent');
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <a href="https://cogslog.com" aria-label="COGSLOG home">
            <img
              src="/logos/stacked/cogslog-stacked-tagline-color.svg"
              alt="COGSLOG"
              className="auth-logo"
            />
          </a>
          <h1 className="login-title">Sign In</h1>
          <p className="login-subtitle">
            Welcome back to COGSLOG
          </p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {error && !unverified && (
            <div id="login-error" className="err-msg" role="alert">
              {error}
            </div>
          )}

          {unverified && (
            <div className="login-verify-notice" role="alert">
              <div className="login-verify-notice__message">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="login-verify-notice__icon">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p>{error} Click the button below to resend your activation email.</p>
              </div>
              {resendStatus === 'sent' ? (
                <p className="login-verify-notice__sent" role="status">
                  Verification email sent. Please check your inbox.
                </p>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleResendVerification}
                  disabled={resendStatus === 'sending'}
                >
                  {resendStatus === 'sending' ? 'Sending...' : 'Resend Verification Email'}
                </button>
              )}
            </div>
          )}

          <div className="field">
            <label htmlFor="email" className="label">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              disabled={submitting}
              autoFocus
              aria-invalid={!!error}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <div className="field">
            <label htmlFor="password" className="label">Password</label>
            <PasswordInput
              id="password"
              name="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
              disabled={submitting}
              aria-invalid={!!error}
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary login-submit"
            disabled={submitting}
          >
            {submitting ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="login-links">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/forgot-password')}
          >
            Forgot Password?
          </button>
        </div>

        <p className="login-signup-link">
          Don&apos;t have an account?{' '}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={async () => {
              try {
                const res = await api.getRegistrationStatus();
                if (res.data.requires_code) {
                  navigate('/register-access');
                } else {
                  navigate('/signup');
                }
              } catch {
                navigate('/signup');
              }
            }}
          >
            Create Account
          </button>
        </p>
      </div>
    </div>
  );
}

export default Login;
